import DOMPurify from 'dompurify';

/**
* @constructor
* Implements a minimal drop-down navigation menu with hamburger menu on mobile / tablet
*
* @param {Element} nav The nav element.
* @param {String} maxwidth the breakpoint for mobile view. Ex 50em
*/

export default function A11yMenu(nav, maxwidth) {
	this.VK_ESC = 27; // escape key
	this.container = nav;
	this.menu = this.container.querySelector('.a11y-nav-wrapper');
	this.trigger = this.container.querySelector('.js-a11y-nav-trigger'); // the hamburger button
	this.closeBtn = this.menu.querySelector('.js-a11y-close-nav-trigger'); // close button
	this.insideNav = this.menu.querySelector('.a11y-nav'); // the main ul

	this.currentSubMenuItem = false;
	this.firstFocusableElement = '';
	this.lastFocusableElement = '';
	this.lastFocusableParentElement = '';
	this.firstSubFocusableElement = '';
	this.lastSubFocusableElement = '';

	// non modale area for inert
	this.bodyToInert = document.querySelectorAll('#page > *:not(header):not(script):not(style), body > *:not(header):not(script):not(style):not(#page)');
	this.siblings = document.querySelectorAll('header > *:not(nav):not(div), .skip-link, .js-a11y-nav-trigger, .brand');

	this.navIsShown = false;

	// bind our methods to the instance
	this.subMenuHandleClick = this.subMenuHandleClick.bind(this);
	this.subMenuHandleBlur = this.subMenuHandleBlur.bind(this);
	this.closeSubAndFocus = this.closeSubAndFocus.bind(this);
	this.makePageInert = this.makePageInert.bind(this);
	this.removePageInert = this.removePageInert.bind(this);
	this.makeNavInert = this.makeNavInert.bind(this);
	this.removeNavInert = this.removeNavInert.bind(this);
	this.openNav = this.openNav.bind(this);
	this.closeNav = this.closeNav.bind(this);
	this.closeSubOnOutsideClick = this.closeSubOnOutsideClick.bind(this);
	this.checkIfMobileNav = this.checkIfMobileNav.bind(this);
	this.handleEscape = this.handleEscape.bind(this);

	this.media = window.matchMedia(`(max-width:${maxwidth})`);
	this.media.addEventListener('change', (e) => {
		this.checkIfMobileNav(e);
	});
}

// MARK: SETUP

// Modify Menu Markup & Bind Listeners
A11yMenu.prototype.menuSetup = function menuSetup() {
	// Check if the menu is in mobile or desktop mode and setup aria
	this.checkIfMobileNav(this.media);

	// SubMenus
	// On transforme les span avec enfants en boutons
	this.insideNav.querySelectorAll('.a11y-nav__sub-menu').forEach((submenu) => {
		if (typeof submenu !== 'undefined') {
			const menuItem = submenu.parentElement.parentElement;
			const focusableElements = submenu.querySelectorAll('a,button');
			const lastfocusableElement = focusableElements[focusableElements.length - 1];
			const closeSubBtn = submenu.querySelector('.js-a11y-close-subNav-trigger');

			// Convert and set
			const button = this.convertSpanToButton(menuItem);
			this.setSubMenuAria(submenu.parentElement, submenu);

			// Bind event listener to button
			button.addEventListener('click', this.subMenuHandleClick);
			lastfocusableElement.addEventListener('blur', this.subMenuHandleBlur);
			if (closeSubBtn) {
				closeSubBtn.addEventListener('click', this.closeSubAndFocus);
			}
		}
	});

	// Event listener for trigger and close Button
	this.trigger.addEventListener('click', this.openNav);
	this.closeBtn.addEventListener('click', this.closeNav);

	// Setup focus trap
	const focusableElements = this.menu.querySelectorAll('a, button');
	const focusableElementsParents = this.menu.querySelectorAll('.a11y-nav > li > a, .a11y-nav > li > button');

	this.firstFocusableElement = focusableElements[0];
	this.lastFocusableElement = focusableElements[focusableElements.length - 1];
	this.lastFocusableParentElement = focusableElementsParents[focusableElementsParents.length - 1];

	// Bind keyboard listener to nav
	this.container.addEventListener('keydown', this.keyboardManager.bind(this));
};

// MARK: Keyboard
A11yMenu.prototype.keyboardManager = function keyboardManager(e) {
	// e.stopImmediatePropagation();

	// ESCAPE
	if (e.keyCode === this.VK_ESC) {
		this.handleEscape();
	}

	// Check for TAB key press if sms nav opened
	if (e.keyCode === 9 && this.navIsShown) {
		// SHIFT + TAB
		if (e.shiftKey) {
			if (this.currentSubMenuItem && document.activeElement === this.firstSubFocusableElement) {
				e.preventDefault();
				this.lastSubFocusableElement.focus();
			} else if (!this.currentSubMenuItem && document.activeElement === this.firstFocusableElement) {
				e.preventDefault();
				this.lastFocusableParentElement.focus();
			}
			// TAB + subMenu open
		} else if (this.currentSubMenuItem && document.activeElement === this.lastSubFocusableElement) {
			e.preventDefault();
			this.firstSubFocusableElement.focus();
		} else if (!this.currentSubMenuItem && document.activeElement === this.lastFocusableParentElement) {
			// TAB + subMenu not open
			e.preventDefault();
			this.firstFocusableElement.focus();
		}
	}
};

A11yMenu.prototype.handleEscape = function handleEscape() {
	// If a subMenu is opened
	if (this.currentSubMenuItem) {
		this.closeSubAndFocus();
	} else if (this.navIsShown) {
		this.closeNav();
	}
};

// MARK: Sub menu

A11yMenu.prototype.closeSubAndFocus = function closeSubAndFocus() {
	if (this.currentSubMenuItem) {
		const button = this.currentSubMenuItem;
		this.toggleSubMenu(button);
		button.focus();
	}
};

A11yMenu.prototype.subMenuHandleBlur = function subMenuHandleBlur() {
	if (this.currentSubMenuItem && !this.navIsShown) {
		const button = this.currentSubMenuItem;
		this.toggleSubMenu(button);
	}
};

A11yMenu.prototype.subMenuHandleClick = function subMenuHandleClick(e) {
	const button = e.currentTarget;

	// close open menu if there is one
	if (this.currentSubMenuItem && button !== this.currentSubMenuItem) {
		this.toggleSubMenu(this.currentSubMenuItem);
	}
	this.toggleSubMenu(button);
};

A11yMenu.prototype.toggleSubMenu = function toggleSubMenu(button) {
	const submenu = button.nextElementSibling;

	if (button.getAttribute('aria-expanded') === 'false') {
		button.setAttribute('aria-expanded', true);
		submenu.setAttribute('aria-hidden', false);
		submenu.removeAttribute('inert');

		this.currentSubMenuItem = button;

		if (this.navIsShown && submenu.querySelector('.smsCloseSubButton')) {
			submenu.querySelector('.smsCloseSubButton').focus();
		}

		window.addEventListener('click', this.closeSubOnOutsideClick);
	} else {
		button.setAttribute('aria-expanded', false);
		submenu.setAttribute('aria-hidden', true);
		submenu.setAttribute('inert', '');
		this.currentSubMenuItem = false;
		window.removeEventListener('click', this.closeSubOnOutsideClick);
	}
};

A11yMenu.prototype.closeSubOnOutsideClick = function closeSubOnOutsideClick(e) {
	const { target } = e;

	// If a subMenu is open, and click is outside, close it
	if (this.currentSubMenuItem && !this.currentSubMenuItem.parentNode.contains(target)) {
		this.closeSubAndFocus();
	}
};

// MARK: Markup

A11yMenu.prototype.convertSpanToButton = function convertSpanToButton(menuItem) {
	const span = menuItem.getElementsByTagName('span')[0];
	const spanAtts = Array.from(span.attributes);
	const button = document.createElement('button');

	if (span !== null && span.getAttribute('tabindex') === '0') {
		button.innerHTML = DOMPurify.sanitize(span.innerHTML);

		spanAtts.forEach((element) => {
			const attr = element;
			// Transfer the attributes other than href.
			if (attr.name !== 'href' && attr.name !== 'tabindex') {
				button.setAttribute(attr.name, attr.value);
			}
		});

		// SetUp Aria for the button
		button.setAttribute('aria-expanded', false);
		menuItem.replaceChild(button, span);
	}
	return button;
};

A11yMenu.prototype.addParentLink = function addParentLink(menuItem, submenu) {
	const parentButtonText = DOMPurify.sanitize(menuItem.querySelector('button').innerHTML).trim();

	// créer le bouton de retour
	const button = document.createElement('button');
	button.innerHTML = parentButtonText;
	button.classList.add('smsCloseSubButton');

	submenu.prepend(button);
	button.addEventListener('click', this.closeSubAndFocus);
};

A11yMenu.prototype.setSubMenuAria = function setSubMenuAria(submenu, ulNav) {
	ulNav.style.transition = 'none';
	submenu.setAttribute('aria-hidden', true);
	submenu.setAttribute('inert', '');

	setTimeout(() => {
		ulNav.style.transition = '';
	}, 500);
};

// MARK: Mobile Nav

A11yMenu.prototype.openNav = function openNav() {
	if (this.navIsShown) {
		this.closeNav();
		return;
	}

	this.menu.style.transition = '';

	this.removeNavInert();
	this.makePageInert();

	document.body.setAttribute('data-burger-menu-status', 'opened');
	this.menu.setAttribute('aria-hidden', false);
	this.navIsShown = true;
};

A11yMenu.prototype.closeNav = function closeNav() {
	this.removePageInert();
	this.makeNavInert();

	this.menu.setAttribute('aria-hidden', true);
	document.body.setAttribute('data-burger-menu-status', 'closed');
	this.navIsShown = false;

	// close open menu if there is one
	if (this.currentSubMenuItem) {
		this.toggleSubMenu(this.currentSubMenuItem);
	}

	setTimeout(() => {
		this.menu.style.transition = 'none';
	}, 500);

	this.trigger.focus();
};

A11yMenu.prototype.checkIfMobileNav = function checkIfMobileNav(e) {
	if (e.matches) {
		// Switch to mobile menu
		this.trigger.removeAttribute('hidden');
		this.menu.setAttribute('aria-hidden', true);
		this.menu.setAttribute('inert', '');
		this.menu.setAttribute('role', 'dialog');
		this.menu.setAttribute('aria-labelledby', 'nav-toggle');
		this.menu.style.transition = 'none';

		this.navIsShown = false;
	} else {
		// Switch to desktop
		this.trigger.setAttribute('hidden', '');
		this.menu.removeAttribute('aria-hidden');
		this.menu.removeAttribute('inert');
		this.menu.removeAttribute('role');
		this.menu.removeAttribute('aria-labelledby');
	}
};

// MARK: Inert

A11yMenu.prototype.makePageInert = function makePageInert() {
	// this.trigger.setAttribute('inert', '');

	this.bodyToInert.forEach((element) => {
		element.setAttribute('inert', '');
	});

	this.siblings.forEach((element) => {
		element.setAttribute('inert', '');
	});
};

A11yMenu.prototype.removePageInert = function removePageInert() {
	// this.trigger.removeAttribute('inert');

	this.bodyToInert.forEach((element) => {
		element.removeAttribute('inert');
	});

	this.siblings.forEach((element) => {
		element.removeAttribute('inert');
	});
};

A11yMenu.prototype.makeNavInert = function makeNavInert() {
	this.menu.setAttribute('inert', '');
};

A11yMenu.prototype.removeNavInert = function removeNavInert() {
	this.menu.removeAttribute('inert');
};

A11yMenu.prototype.focusIsInside = function focusIsInside(element) {
	return element.contains(document.activeElement);
};
